import {
  CONTACT_ORIGIN_VALUES,
  CONTACT_ROLE_VALUES,
  ContactOrigin as ContactOriginEnum,
  ContactRole as ContactRoleEnum,
} from '../../payload/enums/index'
import { getId } from '../../payload/payload.helpers'
import { PayloadContact } from '../../payload/payload-types.zod'
import z from 'zod'

export const ContactRole = ContactRoleEnum
export const ContactOrigin = ContactOriginEnum

export const ContactDbSchema = PayloadContact
export type ContactDb = z.infer<typeof ContactDbSchema>

// Which document a contact was found in. `documentName` is resolved server-side so
// the frontend can link to the source without a second round trip.
export const ContactSourceResponseSchema = z.object({
  documentId: z.string(),
  documentName: z.string().optional(),
  rawText: z.string().optional(),
  foundAt: z.string().optional(),
})

export const ContactResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  organization: z.string().optional(),
  role: z.enum(CONTACT_ROLE_VALUES as [string, ...string[]]).optional(),
  originalRole: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  address: z.string().optional(),
  notes: z.string().optional(),
  origin: z.enum(CONTACT_ORIGIN_VALUES as [string, ...string[]]),
  lockedFields: z.array(z.string()).default([]),
  sources: z.array(ContactSourceResponseSchema).default([]),
  // Convenience for the list view: how many documents mention this contact.
  sourceCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const ContactListResponseSchema = ContactResponseSchema.array()

// Shared editable field surface. Create requires a name; update takes any subset.
//
// Every optional field accepts an empty string as well as null/undefined. Empty
// string is the canonical "clear this field" signal: the generated frontend API
// client flattens `anyOf: [string, null]` down to `string | undefined`, so a
// null-only contract would be untypeable on the client. The service maps both '' and
// null to null when writing. `role` therefore needs '' added to its enum explicitly.
const ContactEditableFields = {
  name: z.string().min(1).max(200),
  organization: z.string().max(200).nullish(),
  role: z
    .union([z.enum(CONTACT_ROLE_VALUES as [string, ...string[]]), z.literal('')])
    .nullish(),
  originalRole: z.string().max(200).nullish(),
  phone: z.string().max(50).nullish(),
  email: z.string().max(200).nullish(),
  address: z.string().max(500).nullish(),
  notes: z.string().max(2000).nullish(),
}

export const ContactCreateRequestSchema = z.object(ContactEditableFields)

// Every field optional — omitted fields are left unchanged. Any field present here
// is also added to the contact's lockedFields, so later AI extraction never
// overwrites it.
export const ContactUpdateRequestSchema = z
  .object({
    name: ContactEditableFields.name.optional(),
    organization: ContactEditableFields.organization,
    role: ContactEditableFields.role,
    originalRole: ContactEditableFields.originalRole,
    phone: ContactEditableFields.phone,
    email: ContactEditableFields.email,
    address: ContactEditableFields.address,
    notes: ContactEditableFields.notes,
    isArchived: z.boolean().optional(),
  })
  .refine(data => Object.keys(data).length > 0, { message: 'No fields to update' })

// Merges `sourceIds` into `targetId`: sources are unioned onto the target, and the
// merged-away contacts are deleted.
export const ContactMergeRequestSchema = z.object({
  targetId: z.string(),
  sourceIds: z.array(z.string()).min(1),
})

export type ContactResponse = z.infer<typeof ContactResponseSchema>
export type ContactListResponse = z.infer<typeof ContactListResponseSchema>
export type ContactCreateRequest = z.infer<typeof ContactCreateRequestSchema>
export type ContactUpdateRequest = z.infer<typeof ContactUpdateRequestSchema>
export type ContactMergeRequest = z.infer<typeof ContactMergeRequestSchema>
export type ContactSourceResponse = z.infer<typeof ContactSourceResponseSchema>

// `documentNames` maps document id -> display name. Callers that populated the
// sources relationship can omit it; unresolved ids simply have no name.
export const contactToResponse = (
  data: ContactDb,
  documentNames?: Record<string, string>,
): ContactResponse => {
  const sources = (data.sources ?? []).map(source => {
    const documentId = getId(source.document)
    const embeddedName =
      typeof source.document === 'string' ? undefined : (source.document?.name ?? undefined)

    return {
      documentId,
      documentName: documentNames?.[documentId] ?? embeddedName,
      rawText: source.rawText ?? undefined,
      foundAt: source.foundAt ?? undefined,
    }
  })

  return ContactResponseSchema.parse({
    id: data.id,
    name: data.name,
    organization: data.organization ?? undefined,
    role: data.role ?? undefined,
    originalRole: data.originalRole ?? undefined,
    phone: data.phone ?? undefined,
    email: data.email ?? undefined,
    address: data.address ?? undefined,
    notes: data.notes ?? undefined,
    origin: data.origin,
    lockedFields: data.lockedFields ?? [],
    sources,
    sourceCount: sources.length,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  } satisfies ContactResponse)
}

export const contactListToResponse = (
  data: ContactDb[],
  documentNames?: Record<string, string>,
): ContactListResponse => data.map(contact => contactToResponse(contact, documentNames))
